import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/map-points`.
 *
 * Tight projection of in-scope locations + their charger counts
 * for the dashboard's destination-map widget. Differs from the
 * tenant-wide `/locations` + `/chargers` list endpoints (which
 * power LocationsPage / ChargersPage / various admin forms) by
 * being scope-aware via AccessAssignment.
 *
 * Why a dedicated endpoint
 * ────────────────────────
 * The map widget's old wiring fanned out to the two list
 * endpoints, both of which filter only by tenant_id with no
 * AccessAssignment lookup. That meant a third_party_admin
 * Building Admin scoped to one location saw pins for every
 * property in their tenant — defeating the entire scoping
 * effort for that one widget. This endpoint plugs the leak
 * without retrofitting the leaky list endpoints (which power
 * other admin surfaces).
 *
 * Sized for the map specifically: `_id`, name, coordinates, and
 * two count fields. No address detail, no contact info, no
 * configuration map — none of which the map needs.
 */
export const DashboardMapPointSchema = z.object({
    _id: z.string(),
    name: z.string(),
    /** Optional because legacy locations may have no coordinates;
     *  the frontend filters those out. */
    coordinates: z.object({
        lat: z.number(),
        lng: z.number(),
    }).nullable(),
    /** Active charger count at this location, scope-filtered. A
     *  third_party_admin with location L1 + chargers A,B (but not
     *  C) sees count = 2 here, not 3 — they shouldn't see
     *  chargers they don't have access to. */
    charger_count: z.number().int().nonnegative(),
    /** Subset of charger_count that's online and recently
     *  heartbeating. Same `is_online + last_heartbeat ≥ now-15min`
     *  formula the operator-summary network ring uses, so the
     *  two surfaces agree on "online right now." */
    online_chargers_count: z.number().int().nonnegative(),
});
export const DashboardMapPointsResponseSchema = z.object({
    rows: z.array(DashboardMapPointSchema),
    is_scoped: z.boolean(),
    generated_at: z.string(),
});
//# sourceMappingURL=dashboard-map-points.js.map